import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import ReactECharts from "echarts-for-react";

import {
  getAnalysisCompare,
  type AnalysisCompareMissionFlight,
  type CompareBundle,
  type CompareMetricRow,
} from "@/api/analysis";
import { getAuthOptions } from "@/api/auth";
import { Cesium3DMap } from "@/components/Cesium3DMap";
import { FilterBar, type FilterSpec } from "@/components/FilterBar";
import { PageHeader } from "@/components/PageHeader";
import { ReplayControls } from "@/components/ReplayControls";
import { RouteMap } from "@/components/RouteMap";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useAuth } from "@/auth/AuthProvider";
import { canReviewAllFlights } from "@/auth/roles";
import { useDelayedFlag, usePulseOnChange } from "@/hooks/useDelayedFlag";
import { useReplayPlayer } from "@/hooks/useReplayPlayer";
import type { TimedPoint } from "@/lib/replay-core";

type BetterDirection = "lower" | "higher" | "neutral";

const FALLBACK_COLORS = [
  "#38bdf8",
  "#f59e0b",
  "#a78bfa",
  "#22c55e",
  "#fb7185",
  "#60a5fa",
  "#facc15",
  "#14b8a6",
];

const METRIC_DIRECTION: Record<string, BetterDirection> = {
  Duration: "lower",
  Distance: "neutral",
  "Avg speed": "higher",
  "Max speed": "higher",
  "Max altitude": "neutral",
  Smoothness: "higher",
  "Peak g": "neutral",
  "Battery spent": "lower",
};

const METRIC_KEY: Record<string, keyof CompareBundle["summary"]> = {
  Duration: "duration_seconds",
  Distance: "distance_m",
  "Avg speed": "avg_speed_kmh",
  "Max speed": "max_speed_kmh",
  "Max altitude": "max_altitude_m",
  Smoothness: "smoothness_score",
  "Peak g": "peak_g",
  "Battery spent": "battery_spent_pct",
};

function formatDurationShort(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const total = Math.max(0, Math.round(seconds));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

function formatDistanceShort(meters: number | null | undefined): string {
  if (meters == null) return "—";
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${Math.round(meters)} m`;
}

function bestIndex(
  row: CompareMetricRow,
  bundles: CompareBundle[],
): number | null {
  if (bundles.length < 2) return null;
  const key = METRIC_KEY[row.label];
  const dir = METRIC_DIRECTION[row.label] ?? "neutral";
  if (!key || dir === "neutral") return null;
  let bestIdx: number | null = null;
  let bestVal: number | null = null;
  bundles.forEach((bundle, idx) => {
    const raw = bundle.summary[key];
    if (typeof raw !== "number") return;
    if (bestVal === null) {
      bestVal = raw;
      bestIdx = idx;
      return;
    }
    if (dir === "lower" ? raw < bestVal : raw > bestVal) {
      bestVal = raw;
      bestIdx = idx;
    }
  });
  return bestIdx;
}

function deltaToneClass(
  row: CompareMetricRow,
  bestIdx: number | null,
): string {
  const dir = METRIC_DIRECTION[row.label] ?? "neutral";
  if (dir === "neutral" || bestIdx === null) {
    return "compare-delta compare-delta--neutral";
  }
  return bestIdx === 1
    ? "compare-delta compare-delta--up"
    : "compare-delta compare-delta--down";
}

function buildDeviationOption(
  series: { session_name: string; color: string; series: number[][] }[],
) {
  const body = document.body ? getComputedStyle(document.body) : null;
  const textMuted = body?.getPropertyValue("--text-muted").trim() || "#A0AAB8";
  const borderColor =
    body?.getPropertyValue("--border").trim() || "rgba(42,52,68,0.5)";
  return {
    grid: { top: 28, right: 20, bottom: 40, left: 48 },
    tooltip: { trigger: "axis" },
    legend: {
      data: series.map((s) => s.session_name),
      top: 0,
      right: 0,
      textStyle: { color: textMuted, fontSize: 11 },
    },
    xAxis: {
      type: "value",
      min: 0,
      max: 100,
      axisLabel: { color: textMuted, formatter: "{value}%" },
      axisLine: { lineStyle: { color: borderColor } },
      name: "Route progress",
      nameLocation: "middle",
      nameGap: 26,
      nameTextStyle: { color: textMuted },
    },
    yAxis: {
      type: "value",
      axisLabel: { color: textMuted, formatter: "{value} m" },
      splitLine: { lineStyle: { color: borderColor, type: "dashed" } },
    },
    series: series.map((s) => ({
      name: s.session_name,
      type: "line",
      data: s.series,
      smooth: 0.2,
      showSymbol: false,
      lineStyle: { width: 2.5, color: s.color },
      itemStyle: { color: s.color },
    })),
  };
}

function readFlightIdsFromParams(params: URLSearchParams): number[] {
  return params
    .getAll("flight_id")
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
}

export function AnalysisComparePage() {
  const { user } = useAuth();
  const canAccessDirectory = canReviewAllFlights(user?.account_type);
  const [params, setParams] = useSearchParams();

  const mission = params.get("mission") ?? undefined;
  const userFilter = params.get("user_id") ?? undefined;
  const flightIdParams = useMemo(
    () => readFlightIdsFromParams(params),
    [params],
  );
  const flightIdParamsKey = flightIdParams.join(",");

  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [
      "analysis",
      "compare",
      { mission, userFilter, flightIds: flightIdParamsKey },
    ],
    queryFn: () =>
      getAnalysisCompare({
        mission,
        user_id: userFilter ? Number(userFilter) : undefined,
        flight_id: flightIdParams.length > 0 ? flightIdParams : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const filterKey = `${mission ?? ""}|${userFilter ?? ""}|${flightIdParamsKey}`;
  const pulse = usePulseOnChange(filterKey, 500);
  const fetchVisible = useDelayedFlag(isFetching && !isPending, 500);
  const showRefetching = !isPending && (pulse || fetchVisible);

  const authOptionsQ = useQuery({
    queryKey: ["auth", "options"],
    queryFn: getAuthOptions,
    staleTime: 5 * 60_000,
  });
  const ionToken = authOptionsQ.data?.cesium_ion_token ?? null;

  // Number of slots in the picker UI. Grows when the user clicks "Add
  // flight"; the URL's flight_id params drive which slots are filled.
  const [slotCount, setSlotCount] = useState(2);

  const displayCount = Math.max(2, slotCount, flightIdParams.length);
  const maxFlights = data?.max_flights ?? 8;

  const baseFlightIds = useMemo<number[]>(() => {
    if (flightIdParams.length > 0) return flightIdParams;
    return data?.selected_flight_ids ?? [];
  }, [flightIdParams, data?.selected_flight_ids]);

  const slotValues: (number | null)[] = useMemo(() => {
    const result: (number | null)[] = [];
    for (let i = 0; i < displayCount; i++) {
      result.push(i < baseFlightIds.length ? baseFlightIds[i] : null);
    }
    return result;
  }, [baseFlightIds, displayCount]);

  function commitSlots(next: (number | null)[]) {
    const ids = next.filter((v): v is number => v != null);
    const nextParams = new URLSearchParams(params);
    nextParams.delete("flight_id");
    ids.forEach((id) => nextParams.append("flight_id", String(id)));
    setParams(nextParams);
    setSlotCount(Math.max(2, next.length));
  }

  function setSlotValue(index: number, value: string) {
    const numeric = value ? Number(value) : null;
    const next = slotValues.slice();
    next[index] = numeric && Number.isFinite(numeric) ? numeric : null;
    commitSlots(next);
  }

  function addSlot() {
    if (displayCount >= maxFlights) return;
    setSlotCount(displayCount + 1);
  }

  function removeSlot(index: number) {
    if (displayCount <= 2) return;
    const next = slotValues.slice();
    next.splice(index, 1);
    commitSlots(next);
  }

  // Build a shared timeline for all flights: pick whichever route has the
  // longest elapsed time as the driver for the replay clock so flights can
  // play to completion in sync.
  const routes3d = data?.payload.routes3d;
  const routes2d = data?.payload.routes2d;
  const timelinePoints = useMemo<TimedPoint[]>(() => {
    let driver: TimedPoint[] = [];
    let driverTotal = 0;
    (routes3d ?? []).forEach((r) => {
      const pts = r.points as unknown as TimedPoint[];
      const total = pts.length ? pts[pts.length - 1].elapsed_seconds : 0;
      if (total > driverTotal) {
        driverTotal = total;
        driver = pts;
      }
    });
    return driver;
  }, [routes3d]);
  const player = useReplayPlayer(timelinePoints);
  const hasReplay = timelinePoints.length > 0;

  const cesiumPrimaryPoints = useMemo<TimedPoint[]>(
    () =>
      routes3d && routes3d.length > 0
        ? (routes3d[0].points as unknown as TimedPoint[])
        : [],
    [routes3d],
  );
  const cesiumPrimaryColor = routes3d?.[0]?.color;
  const cesiumBackgroundPoints = useMemo(
    () =>
      (routes3d ?? []).slice(1).map((r) => ({
        points: r.points as unknown as TimedPoint[],
        color: r.color,
      })),
    [routes3d],
  );
  const routeMapRoutes = useMemo(
    () =>
      (routes2d ?? []).map((r, idx) => ({
        path: r.path,
        color: r.color,
        label: r.session_name,
        points: routes3d?.[idx]
          ? (routes3d[idx].points as unknown as TimedPoint[])
          : undefined,
      })),
    [routes2d, routes3d],
  );

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key === "mission") {
      next.delete("flight_id");
      setSlotCount(2);
    }
    setParams(next);
  }

  if (isPending) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load comparison</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  const {
    missions,
    selected_mission,
    mission_flights,
    users,
    compare_bundles,
    metric_rows,
    payload,
    compare_notice,
  } = data;

  return (
    <>
      <PageHeader
        title="Compare flights"
        lede="Pick flights from the same mission and see their metrics, routes, and telemetry side-by-side."
      />

      <FilterBar
        filters={buildCompareFilters({
          selected_mission,
          missions,
          canAccessDirectory,
          userFilter,
          users,
        })}
        onChange={(key, value) => updateParam(key, value)}
        trailing={
          showRefetching ? (
            <span className="perf-page__loading">Updating…</span>
          ) : null
        }
      />

      <section
        className={`panel compare-flight-picker${showRefetching ? " is-refetching" : ""}`}
        aria-label="Flight selection"
        aria-busy={showRefetching}
      >
        <div className="panel-head">
          <div>
            <h2>Flights</h2>
            <p>
              Select the flights to compare. Add more to overlay additional
              routes on the map.
            </p>
          </div>
          {showRefetching && <span className="panel-refetch">Updating…</span>}
          <button
            type="button"
            className="compare-flight-picker__add"
            onClick={addSlot}
            disabled={
              mission_flights.length === 0 || displayCount >= maxFlights
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Add flight</span>
          </button>
        </div>
        <div className="compare-flight-picker__grid">
          {slotValues.map((selectedId, index) => {
            const bundle = compare_bundles.find(
              (b) => b.summary.flight_id === selectedId,
            );
            const color =
              bundle?.summary.color ??
              FALLBACK_COLORS[index % FALLBACK_COLORS.length];
            const removable = displayCount > 2;
            const options = buildFlightOptions(
              mission_flights,
              slotValues,
              index,
            );
            return (
              <div
                className="compare-flight-picker__slot"
                key={index}
                style={{ ["--flight-color" as string]: color }}
              >
                <div className="compare-flight-picker__slot-head">
                  <span className="compare-flight-picker__slot-label">
                    <span
                      className="compare-flight-picker__slot-dot"
                      aria-hidden
                    />
                    Flight {index + 1}
                  </span>
                  {removable && (
                    <button
                      type="button"
                      className="compare-flight-picker__slot-remove"
                      onClick={() => removeSlot(index)}
                      aria-label={`Remove flight ${index + 1}`}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
                <SearchableSelect
                  className="compare-flight-picker__select"
                  value={selectedId != null ? String(selectedId) : ""}
                  onChange={(v) => setSlotValue(index, v)}
                  minWidth={220}
                  placeholder="Pick a flight…"
                  searchPlaceholder="Search flight…"
                  ariaLabel={`Select flight ${index + 1}`}
                  searchable={mission_flights.length > 6}
                  disabled={mission_flights.length === 0}
                  options={options}
                />
              </div>
            );
          })}
        </div>
      </section>

      {compare_bundles.length > 0 && (
        <section className="compare-vs" aria-label="Selected flights">
          {compare_bundles.map((bundle, idx) => (
            <CompareFlightCard
              key={bundle.summary.flight_id}
              bundle={bundle}
              index={idx}
              fallbackColor={
                bundle.summary.color ??
                FALLBACK_COLORS[idx % FALLBACK_COLORS.length]
              }
            />
          ))}
        </section>
      )}

      {compare_notice && (
        <div className="callout warning">
          <p>{compare_notice}</p>
        </div>
      )}

      {(payload.routes2d.length > 0 || payload.routes3d.length > 0) && (
        <section className="panel compare-maps">
          <div className="panel-head">
            <div>
              <h2>Route replay</h2>
              <p>
                All selected flights plotted on the same 2D map and 3D terrain.
              </p>
            </div>
            <div className="compare-legend" aria-label="Route legend">
              {compare_bundles.map((bundle) => (
                <span
                  key={bundle.summary.flight_id}
                  className="compare-legend__item"
                  style={{ ["--swatch" as string]: bundle.summary.color }}
                >
                  <span className="compare-legend__swatch" />
                  <span className="compare-legend__name">
                    {bundle.summary.session_name}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <div className="replay-split">
            {payload.routes2d.length > 0 && (
              <div className="replay-stage compare-stage" style={{ height: 520 }}>
                <div className="compare-stage__badge">2D · Top-down</div>
                <RouteMap
                  routes={routeMapRoutes}
                  averageRoute={payload.average_route}
                  structures={payload.scenario_structures}
                  homePoint={payload.home_point}
                  height={520}
                  progressElapsed={hasReplay ? player.elapsed : undefined}
                />
              </div>
            )}
            {payload.routes3d.length > 0 && (
              <div className="replay-stage compare-stage" style={{ height: 520 }}>
                <div className="compare-stage__badge">3D · Terrain</div>
                <Cesium3DMap
                  points={cesiumPrimaryPoints}
                  color={cesiumPrimaryColor}
                  backgroundPoints={cesiumBackgroundPoints}
                  structures={payload.scenario_structures}
                  homePoint={payload.home_point}
                  ionToken={ionToken}
                  height={520}
                  progressElapsed={hasReplay ? player.elapsed : undefined}
                  cameraMode="free"
                />
              </div>
            )}
          </div>
          {hasReplay && (
            <div className="compare-replay-controls">
              <ReplayControls
                playing={player.playing}
                elapsed={player.elapsed}
                total={player.total}
                speed={player.speed}
                onPlay={player.play}
                onPause={player.pause}
                onSeek={player.seek}
                onSpeedChange={player.setSpeed}
                onReset={player.reset}
              />
            </div>
          )}
        </section>
      )}

      {payload.deviation_series.length > 0 && (
        <section className="panel">
          <div className="panel-head">
            <h2>Deviation from average</h2>
            <p>
              Perpendicular distance from each selected flight to the average
              route, plotted over normalized route progress.
            </p>
          </div>
          <div style={{ height: 320 }}>
            <ReactECharts
              option={buildDeviationOption(payload.deviation_series)}
              notMerge
              style={{ height: "100%", width: "100%" }}
            />
          </div>
        </section>
      )}

      {compare_bundles.length > 0 && (
        <section className="panel">
          <div className="panel-head">
            <h2>Metric comparison</h2>
            <p>
              {compare_bundles.length === 2
                ? "Winner per row is highlighted. Delta shown as right − left."
                : "Winner per row is highlighted across all selected flights."}
            </p>
          </div>
          <div className="table-shell">
            <table className="data-table compare-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {compare_bundles.map((bundle) => (
                    <th key={bundle.summary.flight_id}>
                      <FlightHeader
                        color={bundle.summary.color}
                        name={bundle.summary.session_name}
                        owner={bundle.summary.owner_label}
                      />
                    </th>
                  ))}
                  {compare_bundles.length === 2 && <th>Delta</th>}
                </tr>
              </thead>
              <tbody>
                {metric_rows.map((row) => {
                  const winnerIdx = bestIndex(row, compare_bundles);
                  const dir = METRIC_DIRECTION[row.label] ?? "neutral";
                  return (
                    <tr key={row.label}>
                      <td className="compare-table__metric">
                        <strong>{row.label}</strong>
                        <small>{row.description}</small>
                      </td>
                      {compare_bundles.map((bundle, idx) => (
                        <td
                          key={bundle.summary.flight_id}
                          className={
                            winnerIdx === idx
                              ? "compare-table__value compare-table__value--winner"
                              : "compare-table__value"
                          }
                        >
                          <span className="compare-table__value-text">
                            {row.values[idx] ?? "—"}
                          </span>
                          {winnerIdx === idx && (
                            <span className="compare-table__winner-chip">
                              Better
                            </span>
                          )}
                        </td>
                      ))}
                      {compare_bundles.length === 2 && (
                        <td>
                          <span className={deltaToneClass(row, winnerIdx)}>
                            {row.delta}
                          </span>
                          {dir === "neutral" && row.delta !== "—" && (
                            <small className="compare-delta__note">
                              informational
                            </small>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function FlightHeader({
  color,
  name,
  owner,
}: {
  color: string;
  name: string;
  owner?: string | null;
}) {
  return (
    <span className="compare-table__flight">
      <span
        className="compare-table__color-dot"
        style={{ background: color }}
        aria-hidden
      />
      <span className="compare-table__flight-meta">
        <span className="compare-table__flight-name">{name}</span>
        {owner && <span className="compare-table__flight-owner">{owner}</span>}
      </span>
    </span>
  );
}

function CompareFlightCard({
  bundle,
  index,
  fallbackColor,
}: {
  bundle: CompareBundle;
  index: number;
  fallbackColor: string;
}) {
  const s = bundle.summary;
  return (
    <div
      className="compare-vs__card"
      style={{ ["--flight-color" as string]: s.color || fallbackColor }}
    >
      <div className="compare-vs__card-head">
        <span className="compare-vs__side-label">Flight {index + 1}</span>
        <span className="compare-vs__color-chip" aria-hidden />
      </div>
      <h3 className="compare-vs__name" title={s.session_name}>
        {s.session_name}
      </h3>
      <div className="compare-vs__meta">
        {s.owner_label && (
          <span className="compare-vs__meta-item">
            <span className="compare-vs__meta-label">Pilot</span>
            <span className="compare-vs__meta-value">{s.owner_label}</span>
          </span>
        )}
        {s.date_display && (
          <span className="compare-vs__meta-item">
            <span className="compare-vs__meta-label">Flown</span>
            <span className="compare-vs__meta-value">{s.date_display}</span>
          </span>
        )}
      </div>
      <div className="compare-vs__stats">
        <div className="compare-vs__stat">
          <span className="compare-vs__stat-label">Duration</span>
          <span className="compare-vs__stat-value">
            {formatDurationShort(s.duration_seconds)}
          </span>
        </div>
        <div className="compare-vs__stat">
          <span className="compare-vs__stat-label">Distance</span>
          <span className="compare-vs__stat-value">
            {formatDistanceShort(s.distance_m)}
          </span>
        </div>
        <div className="compare-vs__stat">
          <span className="compare-vs__stat-label">Avg speed</span>
          <span className="compare-vs__stat-value">
            {s.avg_speed_kmh != null ? `${s.avg_speed_kmh.toFixed(1)} km/h` : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

function buildFlightOptions(
  flights: AnalysisCompareMissionFlight[],
  slotValues: (number | null)[],
  currentIndex: number,
) {
  if (flights.length === 0) {
    return [{ value: "", label: "No flights available" }];
  }
  const pickedElsewhere = new Set(
    slotValues
      .map((v, i) => (i !== currentIndex && v != null ? v : null))
      .filter((v): v is number => v != null),
  );
  return [
    { value: "", label: "None" },
    ...flights.map((f) => ({
      value: String(f.id),
      label: f.session_name,
      hint: `${f.date_display}${f.owner_label ? ` · ${f.owner_label}` : ""}`,
      disabled: pickedElsewhere.has(f.id),
    })),
  ];
}

interface CompareFiltersArgs {
  selected_mission: string | null;
  missions: string[];
  canAccessDirectory: boolean;
  userFilter: string | undefined;
  users: { id: number; username: string; flight_count: number }[];
}

function buildCompareFilters({
  selected_mission,
  missions,
  canAccessDirectory,
  userFilter,
  users,
}: CompareFiltersArgs): FilterSpec[] {
  const list: FilterSpec[] = [
    {
      type: "select",
      key: "mission",
      label: "Mission",
      value: selected_mission ?? "",
      disabled: missions.length === 0,
      options:
        missions.length === 0
          ? [{ value: "", label: "No missions" }]
          : missions.map((m) => ({ value: m, label: m })),
    },
  ];
  if (canAccessDirectory) {
    list.push({
      type: "select",
      key: "user_id",
      label: "Pilot",
      value: userFilter ?? "",
      options: [
        { value: "", label: "All pilots" },
        ...users.map((u) => ({
          value: String(u.id),
          label: `${u.username} (${u.flight_count})`,
        })),
      ],
    });
  }
  return list;
}
